import { Inject, Injectable, Optional } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import { LegacyUserService } from '@rma/dashboard/authentication/public/user';
import { AuthRedirectService } from '@rma/dashboard/authentication/public/util-auth-redirect';
import { ChameleonService } from '@rma/dashboard/generic/util/chameleon';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { LegacyAuthenticationApiService } from '../data-access/legacy-authentication-api.service';
import { OAuthApiService } from '../data-access/oauth-api.service';
import { TokenService } from '../util-token/token.service';
import { TokenProviderService } from '../util-token-provider/token-provider.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private readonly authApiService: OAuthApiService,
    private readonly legacyAuthApiService: LegacyAuthenticationApiService,
    private readonly userService: LegacyUserService,
    private readonly tokenService: TokenService,
    private readonly tokenProviderService: TokenProviderService,
    private readonly authRedirectService: AuthRedirectService,
    @Inject(WA_WINDOW) readonly windowRef: Window,
    @Optional() private readonly chameleonService: ChameleonService | null,
  ) {}

  public login(email: string, password: string): Observable<null> {
    return this.authApiService.login(email, password).pipe(
      switchMap(() => this.authApiService.authorize()),
      // SAFETY(legacy): unreviewed, pre-dates this rule.
      switchMap((x) => this.authApiService.getAccessToken(x.url as string)),
      tap((x) => this.tokenService.persistToken(x)),
      map(() => null),
    );
  }

  public logout() {
    return this.legacyAuthApiService.logout().pipe(
      tap(() => {
        this.userService.refreshUserInfo();
        this.tokenService.terminateToken();
        this.tokenProviderService.clearUserToken();
        this.chameleonService?.clear();
      }),
    );
  }

  public attemptRefreshToken(): Observable<boolean> {
    return this.tokenService.refreshToken().pipe(
      tap((x) => {
        if (!x) {
          this.userService.refreshUserInfo();
        }
      }),
      catchError(() => {
        this.userService.refreshUserInfo();
        return of(false);
      }),
    );
  }

  public getAccessToken(): string | undefined {
    const token = this.tokenService.retrieveToken();

    return token?.access_token ?? this.tokenProviderService.getUserToken() ?? undefined;
  }

  /**
   * @deprecated Only used in JS. Removed once AJS is gone
   */
  public checkUserIsAgencyAdmin() {
    return this.authRedirectService.hasAuthOrRedirect('office').toPromise();
  }

  /**
   * @deprecated Only used in JS. Removed once AJS is gone
   */
  public requireAgent() {
    return this.authRedirectService.hasAuthOrRedirect('individual').toPromise();
  }
}
